/**
 * alerts-zones.ts — the Alerts board's zone presentation (Jeff's rule, 9 October 2026, verbatim):
 *
 *   "Alerts need to be holistic and zone agnostic i.e. alerts from both zones need to be presented
 *    by default, filterable by zone, but always showing a summary legend for each zone."
 *
 * Pure helpers, no React, no I/O — so the board's three promises are testable on their own:
 *   1. every zone's alerts are shown by default, and a zone filter narrows (never targets);
 *   2. a per-zone summary legend is computed over the UNFILTERED rows, so it is the same whatever
 *      the filter shows;
 *   3. a PLATFORM-WIDE alert (`scope: 'platform'` — raised once about a service that serves every
 *      zone, O1 fix 3) is labelled "All zones", survives EVERY zone filter, and is counted ONCE, in
 *      its own "All zones" legend row — never attributed to the zone whose table holds it.
 */
import type { Alert, AlertSeverity } from './alerts-api';
import { ZONE_TABLE, consoleZoneLabel, HOME_ZONE } from './zones';

export const ALL_ZONES_LABEL = 'All zones';
/** The legend row key for platform-wide alerts. */
export const PLATFORM_ROW = 'platform';
/** The legend's Total row key (quality register Q3, 30 October 2026). */
export const TOTAL_ROW = 'total';

export type ZoneTaggedAlert = Pick<Alert, 'severity' | 'status' | 'scope' | 'zone'>;

export const isPlatformWide = (a: Pick<Alert, 'scope'>): boolean => a.scope === 'platform';

/** What the Zone column says for a row: "All zones" for a platform-wide alert, else its zone. */
export function alertZoneLabel(a: Pick<Alert, 'scope' | 'zone'>): string {
  if (isPlatformWide(a)) return ALL_ZONES_LABEL;
  return consoleZoneLabel(a.zone ?? HOME_ZONE.code);
}

/**
 * Does this row survive the zone filter? `'all'` keeps everything. A platform-wide alert survives
 * every zone filter — it IS that zone's alert too. An untagged row counts as the home zone (the
 * same fallback the navigation-bar indicator applies).
 */
export function matchesAlertZone(a: Pick<Alert, 'scope' | 'zone'>, filter: string): boolean {
  if (filter === 'all') return true;
  if (isPlatformWide(a)) return true;
  return (a.zone ?? HOME_ZONE.code) === filter;
}

export interface ZoneLegendRow {
  /** A zone code, or {@link PLATFORM_ROW}. */
  key: string;
  label: string;
  critical: number;
  warning: number;
  info: number;
  acknowledged: number;
  /** Cleared by itself, waiting for an admin to acknowledge it (ADR-071 AM3). */
  autoCleared: number;
  total: number;
}

const empty = (key: string, label: string): ZoneLegendRow => ({ key, label, critical: 0, warning: 0, info: 0, acknowledged: 0, autoCleared: 0, total: 0 });

/**
 * The per-zone legend: one row per zone the platform has (in table order), plus one "All zones"
 * row for platform-wide alerts. Computed over the UNFILTERED list. An acknowledged alert counts
 * under "acknowledged" rather than its severity, and an auto-cleared one under "auto-cleared"
 * (AM3) — the same split the severity pills and `boardCounts` use, so a zone row's Critical is
 * its share of the navbar number.
 *
 * "All zones" is NOT a total — it holds only alerts about a service that serves every zone
 * (Jeff signed the label off on 11 October 2026). On 30 October it read as one: Australia 1,
 * United States 0, All zones 1, against a board of 2. The total is {@link legendTotal}.
 */
export function zoneLegend(alerts: ReadonlyArray<ZoneTaggedAlert>): ZoneLegendRow[] {
  const rows = new Map<string, ZoneLegendRow>();
  for (const z of ZONE_TABLE) rows.set(z.code, empty(z.code, consoleZoneLabel(z.code)));
  rows.set(PLATFORM_ROW, empty(PLATFORM_ROW, ALL_ZONES_LABEL));
  for (const a of alerts) {
    if (a.status === 'cleared') continue;
    const key = isPlatformWide(a) ? PLATFORM_ROW : (a.zone ?? HOME_ZONE.code);
    const row = rows.get(key) ?? rows.set(key, empty(key, consoleZoneLabel(key))).get(key)!;
    row.total += 1;
    if (a.status === 'acknowledged') row.acknowledged += 1;
    else if (a.status === 'auto_cleared') row.autoCleared += 1;
    else row[a.severity as AlertSeverity] += 1;
  }
  return [...rows.values()];
}

/**
 * The legend's Total row (quality register Q3): the column sums of every row. Each alert is in
 * exactly one row, so Total → Critical equals the Critical card and the navbar count.
 */
export function legendTotal(rows: ReadonlyArray<ZoneLegendRow>): ZoneLegendRow {
  const t = empty(TOTAL_ROW, 'Total');
  for (const r of rows) {
    t.critical += r.critical;
    t.warning += r.warning;
    t.info += r.info;
    t.acknowledged += r.acknowledged;
    t.autoCleared += r.autoCleared;
    t.total += r.total;
  }
  return t;
}
